import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Home,
  Search,
  Handshake,
  Key,
  CheckCircle,
  Sparkles,
} from "lucide-react";
import SEO from "@/components/SEO";
import { buildGraph, webPageJsonLd, breadcrumbJsonLd } from "@/data/seo";

const RED = "#D41F30";

const SERVICES = [
  {
    Icon: Search,
    title: "Find the perfect home",
    desc: "Our agents know your market inside out, they'll surface homes that fit your budget, lifestyle, and goals.",
  },
  {
    Icon: Handshake,
    title: "Negotiate like a pro",
    desc: "From the first offer to final concessions, our team gets you the best deal, without the stress of going it alone.",
  },
  {
    Icon: Home,
    title: "Tour & evaluate",
    desc: "Skip the homes that don't fit. Our agents pre-screen listings and walk every property with you.",
  },
  {
    Icon: Key,
    title: "Close with confidence",
    desc: "Coordinated with your CityWorth mortgage team, so there's never a handoff that drops the ball.",
  },
];

const WHY = [
  "Mortgage + real estate under one roof, your lender and agent are on the same team",
  "Local expertise in every market we serve",
  "No pressure. No upselling. We work for you.",
  "Average close time of 21 days from accepted offer",
];

export default function RealEstatePage() {
  return (
    <div className="bg-white" data-testid="real-estate-page">
      <SEO
        title="Real Estate | Your agent and lender, one team | CityWorth"
        description="CityWorth real estate services pair you with a local agent who works side-by-side with your CityWorth mortgage team. One closing, one team, faster results."
        path="/real-estate"
        jsonLd={buildGraph(
          webPageJsonLd({
            name: "CityWorth Real Estate",
            description: "Real estate agents and mortgage lender on the same team.",
            path: "/real-estate",
          }),
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Real Estate", path: "/real-estate" },
          ])
        )}
      />
      {/* Hero */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-red-50 rounded-full blur-3xl opacity-50 -translate-y-1/3 -translate-x-1/3 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-red-50 rounded-full px-4 py-2 mb-6">
                <Home className="w-4 h-4" style={{ color: RED }} />
                <span className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: RED }}>
                  CityWorth Real Estate
                </span>
              </div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-[1.02] tracking-tight mb-6">
                Your agent and lender, <span style={{ color: RED }}>on the same team.</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-10 max-w-2xl">
                Most buyers spend weeks hunting for a realtor, then start over for financing. At CityWorth, your real estate agent and mortgage expert work side-by-side from the first showing to the final signature.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => window.location.assign("/get-started")}
                  data-testid="real-estate-cta"
                >
                  Start my homebuying plan
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-8 py-7 text-lg font-semibold"
                  onClick={() => document.getElementById("services").scrollIntoView({ behavior: "smooth" })}
                >
                  See our services
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1200&q=80"
                  alt="Real estate agent showing a home"
                  className="w-full h-[440px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              What we do
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 leading-tight">
              Full-service real estate, end to end.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-gray-100 p-8 shadow-sm hover:shadow-lg transition-shadow"
                data-testid={`service-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-5">
                  <s.Icon className="w-6 h-6" style={{ color: RED }} />
                </div>
                <h3 className="font-display text-2xl font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl order-2 lg:order-1">
            <img loading="lazy"
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80"
              alt="Happy homebuyers"
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-xs font-bold tracking-[0.18em] uppercase mb-3" style={{ color: RED }}>
              Why CityWorth Real Estate
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-8">
              One team. One process. One closing.
            </h2>
            <ul className="space-y-4">
              {WHY.map((w, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-lg text-gray-700">{w}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Button
                className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-8 py-6 text-base font-semibold"
                onClick={() => window.location.assign("/get-started")}
              >
                Get connected with an agent
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-red-50 to-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#D41F30] flex items-center justify-center mb-6">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-[1.05] tracking-tight">
            Ready to find your home?
          </h2>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Tell us where you want to live and what you're looking for. We'll match you with the right agent and lender, same team, same goal.
          </p>
          <Button
            className="bg-[#D41F30] hover:bg-[#B91C2A] text-white rounded-full px-10 py-7 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            onClick={() => window.location.assign("/get-started")}
            data-testid="real-estate-cta-bottom"
          >
            Start my homebuying plan
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
